import React, { useEffect, useState } from "react";
import {
  Link
} from "react-router-dom";
import {
  Plus,
  Pencil,
  Power,
  AlertCircle,
  CheckCircle2,
  FileText
} from "lucide-react";
import * as ownerApi from "../../api/payroll/leaveTypeApi";
import * as portalApi from "../../api/payroll/leaveTypePortalApi";
import {
  Button,
  IconButton,
  Card,
  CardContent,
  Table,
  Badge,
  EmptyState,
  Skeleton
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

export default function LeaveTypeList({ isPortal = false }) {
  const api = isPortal ? portalApi : ownerApi;
  const basePath = isPortal ? "/staff-panel/leave-types" : "/leave-types";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getAllLeaveTypes();
      const list = Array.isArray(res) ? res : res.data || [];
      setItems(list);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load leave types");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [isPortal]);

  const handleToggle = async (id) => {
    try {
      const res = await api.toggleLeaveTypeActive(id);
      toast.success(res.message || "Updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Toggle failed");
    }
  };

  const columns = [
    {
      key: "name",
      header: "Name",
      render: (row) => (
        <span className="font-semibold text-neutral-900 dark:text-neutral-100">{row.name}</span>
      ),
    },
    {
      key: "code",
      header: "Code",
      render: (row) => (
        <Badge variant="secondary" className="font-mono text-[11px]">
          {row.code}
        </Badge>
      ),
    },
    {
      key: "isPaid",
      header: "Paid",
      render: (row) => (
        <Badge variant={row.isPaid ? "success" : "warning"}>
          {row.isPaid ? "Paid" : "Unpaid (LOP)"}
        </Badge>
      ),
    },
    {
      key: "maxDaysPerYear",
      header: "Max / Year",
      render: (row) => (
        <span className="font-semibold text-neutral-700 dark:text-neutral-200">
          {row.maxDaysPerYear ?? "—"}
        </span>
      ),
    },
    {
      key: "active",
      header: "Status",
      render: (row) => (
        <Badge variant={row.active ? "success" : "neutral"}>
          {row.active ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (row) => (
        <div className="flex items-center justify-end gap-1">
          <Link to={`${basePath}/edit/${row._id}`}>
            <IconButton aria-label="Edit Leave Type" variant="ghost" size="sm">
              <Pencil className="h-4 w-4 text-neutral-600 dark:text-neutral-300 hover:text-primary-600" />
            </IconButton>
          </Link>
          <IconButton
            aria-label="Toggle Status"
            variant="ghost"
            size="sm"
            onClick={() => handleToggle(row._id)}
          >
            <Power className="h-4 w-4 text-neutral-600 dark:text-neutral-300 hover:text-warning-700" />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
      {/* Top Navigation & Title Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-neutral-200 dark:border-neutral-800">
        <div>
          <h1 className="text-title-lg md:text-headline-lg font-display font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
            Leave Types
          </h1>
          <p className="text-body-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Configure leave quotas, paid status, and payroll deduction rules
          </p>
        </div>

        <Link to={`${basePath}/create`}>
          <Button variant="primary" leftIcon={<Plus className="h-4 w-4" />}>
            Add Leave Type
          </Button>
        </Link>
      </div>

      

      

      {loading ? (
        <Card>
          <CardContent className="p-6 space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex gap-4">
                <Skeleton height={20} width="20%" />
                <Skeleton height={20} width="15%" />
                <Skeleton height={20} width="15%" />
                <Skeleton height={20} width="20%" />
                <Skeleton height={20} width="15%" />
                <Skeleton height={20} width="15%" />
              </div>
            ))}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table
              columns={columns}
              data={items}
              emptyState={
                <EmptyState
                  icon={FileText}
                  title="No leave types created yet"
                  description="Get started by creating your first leave type."
                  action={
                    <Link to={`${basePath}/create`}>
                      <Button variant="primary" size="sm" leftIcon={<Plus className="h-4 w-4" />}>
                        Add Leave Type
                      </Button>
                    </Link>
                  }
                />
              }
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}